import React, { Component } from "react";
import PropTypes from "prop-types";
import { AnnouncementService } from "@services";
import { Link } from "react-router";
import utils from "@utils";
import ProgressButton from "react-progress-button";
import { Loader } from "../../../components";
import "./AnnouncementForm.scss";

class AnnouncementForm extends Component {
	constructor(props) {
		const {
			params: { announcementId = "add" },
		} = props;
		const editMode = announcementId !== "add";
		const { lang } = global;

		super(props);
		this.state = {
			editMode,
			isLoading: editMode,
			announcementId: editMode ? announcementId : null,
			buttonState: "",
			form: {
				Subject: "",
				Body: "",
				ActiveStartDateTime: "",
				ActiveEndDateTime: "",
				SeverityType: "",
				AudienceTypes: [],
				NeedNotification: "",
			},
			isValidPreview: true,
		};

		this.audiencePortals = [
			{ description: lang.t("All Portals"), value: "10" },
			{ description: lang.t("Agency Portal"), value: "20" },
			{ description: lang.t("Coordinator Portal"), value: "30" },
			{ description: lang.t("Client Portal"), value: "40" },
			{ description: lang.t("Mobile App"), value: "50" },
		];

		this.audienceUsers = [
			{ description: lang.t("Admin"), value: "60" },
			{ description: lang.t("Officer"), value: "70" },
			{ description: lang.t("Client"), value: "80" },
			{ description: lang.t("Security Agency"), value: "90" },
			{ description: lang.t("LEA"), value: "100" },
			{ description: lang.t("Coordinator"), value: "110" },
		];

		this.previewTimer = null;
	}

	componentDidMount() {
		const { lang } = global;
		const { form } = this.state;
		const {
			params: { announcementId = "add" },
			router,
		} = this.props;
		const editMode = announcementId !== "add";

		if (editMode) {
			this.setState({ isLoading: true });

			AnnouncementService.getAnnouncementById(announcementId)
				.then(({ data }) => {
					const { ActiveStartDateTime, ActiveEndDateTime } = data;
					this.setState({
						form: {
							...form,
							...data,
							ActiveStartDateTime:
								this.convertUTCToLocalTimestamp(ActiveStartDateTime),
							ActiveEndDateTime: this.convertUTCToLocalTimestamp(ActiveEndDateTime),
							SeverityType: data.SeverityType,
							AudienceTypes: data.AudienceTypes.map(String),
						},
						isLoading: false,
					});
				})
				.catch(error => {
					utils.notify({
						type: "error",
						message:
							error.status === 404
								? lang.t("Announcement not found")
								: lang.t("Error loading Announcement details"),
						timeOut: 1500,
					});
					if (error.status === 404) router.replace("/announcements");

					this.setState({ isLoading: false });
				});
		}
	}

	/**
	 * Converts Microsoft SQL UTC timestamps into ones local to the user's system
	 * @param {String} timestamp A UTC timestamp value
	 * @returns {String} A Microsoft SQL-formatted timestamp in the user's local timezone
	 */
	convertUTCToLocalTimestamp = timestamp => {
		const { user } = this.props;
		const cleaned = `${timestamp}${timestamp.substring(timestamp.length - 1).toLowerCase() !== "z" ? "Z" : ""}`;
		const date = utils.formatDate(cleaned, undefined, user.TimeZoneId);
		const time = utils.formatTime(cleaned, false, user.TimeZoneId);
		return `${date}T${time}`;
	};

	convertTimezoneOffsetToString = () => {
		const tzOffset = new Date().getTimezoneOffset();
		let offset = tzOffset < 0 ? "+" : "-";
		const offsetHrs = Math.floor(tzOffset / 60);
		const offsetMin = tzOffset % 60;
		offset += `${offsetHrs < 10 ? "0" : ""}${offsetHrs}:${offsetMin < 10 ? "0" : ""}${offsetMin}`;
		return offset;
	};

	handleOnChange = name => e => {
		const {
			form,
			form: { AudienceTypes },
		} = this.state;
		const { target } = e;
		let newValue = target.value;

		switch (name) {
			case "NeedNotification":
				newValue = target.checked;
				break;
			case "SeverityType":
				newValue = target.checked ? parseInt(target.value, 10) : "";
				break;
			case "AudienceTypes":
				newValue = AudienceTypes;
				if (newValue.indexOf(target.value) < 0) {
					newValue.push(target.value);
				} else {
					newValue.splice(newValue.indexOf(target.value), 1);
				}
				break;
			default:
				break;
		}

		form[name] = newValue;

		this.setState({ form });
	};

	/**
	 * Gathers and submits the updated form values after being confirmation of being valid
	 * @param {Event} e An event to prevent event bubbling
	 */
	handleOnSave = e => {
		e.preventDefault();
		const { lang } = global;
		const { router } = this.props;
		const {
			announcementId,
			form: {
				Subject,
				Body,
				ActiveStartDateTime,
				ActiveEndDateTime,
				SeverityType,
				AudienceTypes,
				NeedNotification,
			},
		} = this.state;

		if (!this.validateForm()) {
			return;
		}

		AnnouncementService.saveAnnouncement({
			Id: announcementId,
			ActiveStartDateTime: `${ActiveStartDateTime}${this.convertTimezoneOffsetToString()}`,
			ActiveEndDateTime: `${ActiveEndDateTime}${this.convertTimezoneOffsetToString()}`,
			Subject,
			Body,
			SeverityType,
			AudienceTypes,
			NeedNotification,
		})
			.then(() => {
				const editMode = announcementId !== null;

				utils.notify({
					type: "success",
					message: editMode
						? lang.t("Announcement successfully updated")
						: lang.t("Announcement successfully created"),
				});

				this.updateProgressButton("success", () => {
					router.push("/announcements");
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Announcement details"),
					title: "Error",
				});

				this.setState({ isLoading: false });
				this.updateProgressButton("error");
			});
	};

	updateProgressButton = (value, callback) => {
		utils.updateProgressButton({
			context: this,
			property: "buttonState",
			value,
			callback,
		});
	};

	/**
	 * Validates each fomr input an ensures all user input is formatted correctly
	 * @returns {Boolean} Determines if the form inputs are valid
	 */
	validateForm = () => {
		const {
			form: { Subject, Body, ActiveStartDateTime, ActiveEndDateTime, SeverityType },
		} = this.state;
		const { lang } = global;
		let formValid = false;

		formValid = !(
			Subject === "" ||
			Body === "" ||
			ActiveStartDateTime === "" ||
			ActiveEndDateTime === "" ||
			SeverityType === ""
		);

		if (formValid) {
			const startDateTime = new Date(ActiveStartDateTime).getTime();
			const endDateTime = new Date(ActiveEndDateTime).getTime();

			if (startDateTime < endDateTime) {
				formValid = true;
			} else {
				utils.notify({
					type: "error",
					message: lang.t("End date must be greater than Start date"),
				});
				formValid = false;
			}
		}
		if (!formValid) {
			this.updateProgressButton("error");
		}

		return formValid;
	};

	/**
	 * Applies styles to the text body of the announcement
	 * @param {String} style The type of styling which will be applied to the text in Body input field
	 */
	applyTextStyle = style => () => {
		const {
			form: { Body },
		} = this.state;
		const cursorStart = this.bodyInputRef.selectionStart;
		const cursorEnd = this.bodyInputRef.selectionEnd;
		let text = Body.substring(cursorStart, cursorEnd).trim();

		if (text === "") return;

		if (style === "bold") {
			text = `**${text}**`;
		} else if (style === "italic") {
			text = `_${text}_`;
		} else if (style === "link") {
			text = `[${text}](${text})`;
		}

		this.setState(state => ({
			form: {
				...state.form,
				Body: `${Body.substring(0, cursorStart)}${text}${Body.substring(cursorEnd, Body.length)}`,
			},
		}));
	};

	setMessagePreview = () => {
		const {
			form: { ...form },
		} = this.state;
		const { previewAnnouncement } = this.props;
		if (form.Body && form.Subject && form.SeverityType) {
			// Transform the data so that start and end times do not cause the message to be displayed
			form.Id = "announcement-preview";
			form.ActiveStartDateTime = `${utils.formatDate(Date.now(), undefined, undefined, "2-digit")}T00:00`;
			form.ActiveEndDateTime = `${utils.formatDate(Date.now() + 86400000, undefined, undefined, "2-digit")}T00:00`;
			previewAnnouncement([form], () => {
				this.previewTimer = setTimeout(() => {
					clearTimeout(this.previewTimer);
					previewAnnouncement([]);
				}, 5000);
			});
		}
		this.setState({ isValidPreview: form.Body && form.Subject && form.SeverityType });
	};

	render() {
		const { lang } = global;
		const {
			editMode,
			isLoading,
			buttonState,
			announcementId,
			form: {
				Body,
				Subject,
				ActiveStartDateTime,
				ActiveEndDateTime,
				SeverityType,
				AudienceTypes,
				NeedNotification,
			},
			isValidPreview,
		} = this.state;
		const pageTitle = `${editMode ? lang.t("Edit") : lang.t("Add")} ${lang.t("Announcement")}`;

		return (
			<div
				className="container"
				id="announcement-form"
			>
				<div className="row">
					<h2 className="col-sm-12">{pageTitle}</h2>
				</div>
				<div className="row">
					<div className="col-sm-10 col-sm-offset-1">
						<form className="form-horizontal">
							<If condition={!announcementId}>
								<div className="form-group">
									<div className="col-sm-10 col-sm-offset-2">
										<input
											type="checkbox"
											name="NeedNotification"
											id="NeedNotification"
											value={NeedNotification}
											checked={NeedNotification}
											onChange={this.handleOnChange("NeedNotification")}
										/>
										<label htmlFor="NeedNotification">
											<strong>{lang.t("Send Notification")}</strong>
										</label>
									</div>
								</div>
							</If>
							<div className="form-group">
								<div className="col-sm-2">
									<label
										className="control-label"
										htmlFor="Subject"
									>{`${lang.t("Title")}:`}</label>
								</div>
								<div className="col-sm-10">
									<input
										name="Subject"
										id="Subject"
										className="form-control"
										value={Subject}
										onChange={this.handleOnChange("Subject")}
									/>
								</div>
							</div>
							<div className="form-group">
								<div className="col-sm-2">
									<label
										className="control-label"
										htmlFor="Body"
									>{`${lang.t("Body")}:`}</label>
								</div>
								<div className="col-sm-10">
									<textarea
										ref={ref => {
											this.bodyInputRef = ref;
										}}
										name="Body"
										id="Body"
										// eslint-disable-next-line react/no-unknown-property
										row="4"
										className="form-control"
										value={Body}
										onChange={this.handleOnChange("Body")}
									/>
								</div>
								<div className="col-sm-10 col-sm-offset-2 format-options">
									<div>
										<button
											type="button"
											onClick={this.applyTextStyle("bold")}
											title={lang.t("Bold")}
										>
											<i className="glyphicon glyphicon-bold" />
										</button>
										<button
											type="button"
											onClick={this.applyTextStyle("italic")}
											title={lang.t("Emphasis")}
										>
											<i className="glyphicon glyphicon-italic" />
										</button>
										<button
											type="button"
											onClick={this.applyTextStyle("link")}
											title={lang.t("Link")}
										>
											<i className="glyphicon glyphicon-link" />
										</button>
									</div>
									<div className="text-right">
										<small>
											{lang.t("Use **Text** for bold")}
											<strong>{lang.t("Text")}</strong>
										</small>
										<br />
										<small>
											{lang.t("Use _Text_ for emphasis")}
											<em>{lang.t("Text")}</em>
										</small>
										<br />
										<small>
											{lang.t("Use [RollKall dot com] for links")}&nbsp;
											<a
												href="http://rollkall.com"
												target="_blank"
												rel="noreferrer noopener"
											>
												RollKall
											</a>
										</small>
									</div>
								</div>
							</div>
							<div className="row form-group schedule">
								<div className="col-sm-4 col-sm-offset-2">
									<label htmlFor="ActiveStartDateTime">{`${lang.t("Display From")}`}</label>
									<input
										type="datetime-local"
										name="ActiveStartDateTime"
										id="ActiveStartDateTime"
										className="form-control"
										value={ActiveStartDateTime}
										onChange={this.handleOnChange("ActiveStartDateTime")}
									/>
								</div>
								<div className="col-sm-4">
									<label htmlFor="ActiveEndDateTime">{`${lang.t("Display Until")}`}</label>
									<input
										type="datetime-local"
										name="ActiveEndDateTime"
										id="ActiveEndDateTime"
										className="form-control"
										value={ActiveEndDateTime}
										onChange={this.handleOnChange("ActiveEndDateTime")}
									/>
								</div>
							</div>
							<div className="form-group">
								<div className="col-sm-2">
									<span className="control-label">{lang.t("Display Type")}:</span>
								</div>
								<div className="col-sm-10">
									<label
										className="radio-inline"
										htmlFor="SeverityType-info"
									>
										<input
											name="SeverityType"
											id="SeverityType-info"
											type="radio"
											value="10"
											checked={SeverityType === 10}
											onChange={this.handleOnChange("SeverityType")}
										/>
										<span>{lang.t("Information")}</span>
									</label>
									<label
										className="radio-inline"
										htmlFor="SeverityType-warn"
									>
										<input
											name="SeverityType"
											id="SeverityType-warn"
											type="radio"
											value="20"
											checked={SeverityType === 20}
											onChange={this.handleOnChange("SeverityType")}
										/>
										<span>{lang.t("Warning")}</span>
									</label>
									<label
										className="radio-inline"
										htmlFor="SeverityType-err"
									>
										<input
											name="SeverityType"
											id="SeverityType-err"
											type="radio"
											value="30"
											checked={SeverityType === 30}
											onChange={this.handleOnChange("SeverityType")}
										/>
										<span>{lang.t("Error")}</span>
									</label>
								</div>
							</div>
							<div className="form-group">
								<div className="col-sm-2 col-sm-offset-2">
									<button
										type="button"
										className="btn btn-default btn-block"
										onClick={this.setMessagePreview}
									>
										{lang.t("Preview")}
									</button>
								</div>
								<div className="col-sm-8">
									<span
										className={`text-danger${isValidPreview ? " hidden" : ""}`}
									>
										{lang.t(
											"Announcement preview requires a Title, Body, and Display Type to appear",
										)}
									</span>
								</div>
							</div>
							<div className="form-group">
								<div className="col-sm-4 col-sm-offset-2">
									<strong>{lang.t("Portals")}</strong>
									{this.audiencePortals.map(portal => (
										<div
											className="checkbox"
											key={`portal-${portal.value}`}
										>
											<input
												type="checkbox"
												name="AudienceTypes"
												id={portal.description.replace(/\s/g, "-")}
												value={portal.value}
												checked={AudienceTypes.includes(portal.value)}
												onChange={this.handleOnChange("AudienceTypes")}
											/>
											<label htmlFor={portal.description.replace(/\s/g, "-")}>
												{portal.description}
											</label>
										</div>
									))}
								</div>
								<div className="col-sm-4">
									<strong>{lang.t("Users")}</strong>
									{this.audienceUsers.map(user => (
										<div
											className="checkbox"
											key={`user-${user.value}`}
										>
											<input
												type="checkbox"
												name="AudienceTypes"
												id={user.description.replace(/\s/g, "-")}
												value={user.value}
												checked={AudienceTypes.includes(user.value)}
												onChange={this.handleOnChange("AudienceTypes")}
											/>
											<label htmlFor={user.description.replace(/\s/g, "-")}>
												{user.description}
											</label>
										</div>
									))}
								</div>
							</div>
						</form>
					</div>
				</div>
				<div className="actions text-center">
					<Link
						className="btn btn-link"
						to="/announcements"
					>
						{lang.t("Cancel")}
					</Link>
					<ProgressButton
						className="btn btn-primary"
						state={buttonState}
						onClick={e => this.handleOnSave(e)}
					>
						{lang.t("Save")}
					</ProgressButton>
				</div>
				<Loader active={isLoading} />
			</div>
		);
	}
}

AnnouncementForm.propTypes = {
	router: PropTypes.object.isRequired,
	params: PropTypes.object,
	user: PropTypes.object,
	previewAnnouncement: PropTypes.func,
};

AnnouncementForm.defaultProps = {
	params: {},
	user: {},
	previewAnnouncement: () => {},
};

export default AnnouncementForm;
